import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, getDocs, where, query, collection, serverTimestamp } from 'firebase/firestore';
import { app, auth, db } from '../firebaseConfig';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebaseConfig';
// Navigation uses window.location.href for reliability
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { getFunctions, httpsCallable } from "firebase/functions";
import {sendPasswordResetEmail, verifyBeforeUpdateEmail, sendEmailVerification} from "firebase/auth";
import config from '../config/environment';
import { getAgeString } from '../utils/dateHelpers';
import InfoTip from './InfoTip';

const functions = getFunctions(app);
const createStripeAccount = httpsCallable(functions, "createStripeAccount");
const attachPayment = httpsCallable(functions, 'attachPayment');
const checkStripeAccountCapabilities = httpsCallable(functions, 'checkStripeAccountCapabilities');

// Create Stripe promise once
const publishableKey = config.getStripePublishableKey();
console.log("Using Stripe publishable key:", publishableKey.substring(0, 20) + "...");
const stripePromise = loadStripe(publishableKey);

const EditProfile = () => {
  const user = auth.currentUser;
  // Navigation uses window.location.href for reliability

  const [userData, setUserData] = useState(null);
  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [experience, setExperience] = useState('');
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePictureURL, setProfilePictureURL] = useState('');
  const [profilePicturePreview, setProfilePicturePreview] = useState('');
  const [numberOfChildren, setNumberOfChildren] = useState(0);
  const [children, setChildren] = useState([]); // Changed from childrenAges to children with DOB
  const [address, setAddress] = useState('');
  const [streetAddress, setStreetAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [error, setError] = useState('');
  const [certifications, setCertifications] = useState([]);
  const [certificationFiles, setCertificationFiles] = useState({});
  const [isStripeConnected, setIsStripeConnected] = useState(false);
  const [stripeCustomerId, setStripeCustomerId] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [emailNotifications, setEmailNotifications] = useState(true);

  // const stripePromise = loadStripe("pk_live_51HDdEjFzYsoDjWsuIsGxV1Ln8SSjEntOi1JeOvtbsTAZ6N19k46HPgejbXA8UNglzOOmN94SJL0FywmUzI6P7rCC00w771w8S7");

  // const [cardNumber, setCardNumber] = useState(''); // Unused
  // const [expiryDate, setExpiryDate] = useState(''); // Unused
  // const [cvv, setCvv] = useState(''); // Unused
  // const [cardError, setCardError] = useState(''); // Unused
  

  useEffect(() => {
    const fetchUserProfile = async () => {
      const docRef = doc(db, 'users', user.uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        setUserData(data);
        setName(data.name);
        setBio(data.bio);
        setDateOfBirth(data.dateOfBirth || '');
        setExperience(data.experience || '');
        setEmailNotifications(data.emailNotifications !== false); // default true
        setCertifications(data.certifications || []);
        setProfilePictureURL(data.profilePictureURL || '');
        setProfilePicturePreview(data.profilePictureURL || '');
        setNumberOfChildren(data.numberOfChildren || 0);
        setAddress(data.address || '');
        setStreetAddress(data.streetAddress || '');
        setCity(data.city || '');
        setState(data.state || '');
        setZipCode(data.zipCode || '');
        setStripeCustomerId(data.stripeCustomerId || '');
        setIsStripeConnected(data.stripeAccountId && data.stripeAccountId.trim() !== "" && data.stripeAccountStatus === 'completed'); // Check if the sitter has completed onboarding
        console.log(data.stripeAccountId)

        // If the sitter's Stripe account appears incomplete, silently re-check in case the webhook was missed
        if (data.userType === 'sitter' && data.stripeAccountId && data.stripeAccountStatus === 'pending_onboarding') {
          checkStripeAccountCapabilities({ stripeAccountId: data.stripeAccountId }).then((result) => {
            if (result.data?.isFullyOnboarded) {
              setIsStripeConnected(true);
              setUserData((prev) => ({ ...prev, stripeAccountStatus: 'completed' }));
            }
          }).catch(() => {/* silently ignore */});
        }

        // Handle both old format (childrenAges) and new format (children with DOB)
        if (data.children && Array.isArray(data.children)) {
          setChildren(data.children);
          setNumberOfChildren(data.children.length);
        } else if (data.childrenAges) {
          // Convert old format to new format (set DOB as empty for migration)
          const convertedChildren = data.childrenAges.map((age, index) => ({
            id: `child-${index}`,
            name: '',
            dateOfBirth: '',
            // Store old age data for reference during migration
            legacyAge: age
          }));
          setChildren(convertedChildren);
          setNumberOfChildren(data.childrenAges.length);
        }
        
      }
    };

    if (user) {
      fetchUserProfile();
      user.reload();
    }
  }, [user]);

  // Cleanup preview URL when component unmounts
  useEffect(() => {
    return () => {
      if (profilePicturePreview && profilePicturePreview !== profilePictureURL) {
        URL.revokeObjectURL(profilePicturePreview);
      }
    };
  }, [profilePicturePreview, profilePictureURL]);

  const validateAddress = async (address) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${config.getGoogleMapsApiKey()}`
      );
      const data = await response.json();
      if (data.status === 'OK' && data.results.length > 0) {
        const result = data.results[0];
        const addressComponents = result.address_components;
        const requiredComponents = ['street_number', 'route', 'locality', 'administrative_area_level_1', 'country', 'postal_code'];
        const providedComponents = addressComponents.map(component => component.types[0]);
        const isFullAddress = requiredComponents.every(component => providedComponents.includes(component));

        return isFullAddress ? result.formatted_address : null;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error validating address:', error);
      return null;
    }
  };


  const handleSaveProfile = async () => {
    if (userData) {
      if (!name.trim()) {
        alert('Name is required.');
        return;
      }

      if (!streetAddress.trim() || !city.trim() || !state.trim() || !zipCode.trim()) {
        alert('Please fill in street address, city, state, and ZIP code.');
        return;
      }

      const addressToValidate = `${streetAddress.trim()} ${city.trim()}, ${state.trim()} ${zipCode.trim()}`;
      const validated = await validateAddress(addressToValidate);
      if (!validated) {
        alert('Invalid address. Please enter a complete, valid street address (number, street, city, state, ZIP).');
        return;
      }
      setError('');

      // Parents: every child must have a date of birth
      if (userData.userType === 'parent' && children && children.length > 0) {
        const missingDob = children.findIndex((c) => !c?.dateOfBirth);
        if (missingDob !== -1) {
          alert(`Please enter a date of birth for Child ${missingDob + 1}.`);
          return;
        }
      }

      // For parents: require a valid card on file before saving profile
      if (userData.userType === "parent") {
        if (!userData.paymentMethodId || userData.paymentMethodId.trim() === "") {
          alert("You must have a valid payment method (card) on file to save your profile. Please add a card first.");
          return;
        }
      }

      try {
        let profilePictureURLToUpdate = profilePictureURL;

        if (profilePicture) {
          const storageRef = ref(storage, `profilePictures/${user.uid}`);
          await uploadBytes(storageRef, profilePicture);
          profilePictureURLToUpdate = await getDownloadURL(storageRef);
        }

        // Upload certification files
        const normalizeCertName = (c) => {
          if (!c) return '';
          if (typeof c === 'string') return c;
          if (typeof c === 'object' && typeof c.name === 'string') return c.name;
          if (typeof c === 'object' && c.name && typeof c.name === 'object' && typeof c.name.name === 'string') return c.name.name;
          return '';
        };
        const certificationURLs = certifications.map((c) => {
          if (c && typeof c === 'object' && c.fileURL) {
            return { name: normalizeCertName(c), fileURL: c.fileURL };
          }
          return { name: normalizeCertName(c), fileURL: '' };
        });
        for (const [index, file] of Object.entries(certificationFiles)) {
          if (file) {
            const certRef = ref(storage, `certifications/${user.uid}/${file.name}`);
            await uploadBytes(certRef, file);
            const fileURL = await getDownloadURL(certRef);
            certificationURLs[index] = { name: normalizeCertName(certifications[index]), fileURL };
          }
        }

        // Drop any empty cert rows (no name AND no file) so they don't pollute the saved data
        const filteredCertifications = certificationURLs.filter((c) => {
          const hasName = c && typeof c.name === 'string' && c.name.trim() !== '';
          const hasFile = c && typeof c.fileURL === 'string' && c.fileURL !== '';
          return hasName || hasFile;
        });

        const userRef = doc(db, 'users', user.uid);
        const fullAddress = `${streetAddress} ${city}, ${state} ${zipCode}`;

        await updateDoc(userRef, {
          name,
          bio,
          dateOfBirth,
          experience,
          emailNotifications,
          certifications: filteredCertifications,
          address: fullAddress,
          streetAddress,
          city,
          state,
          zipCode, // Save the updated address
          profilePictureURL: profilePictureURLToUpdate,
          children, // Save children with date of birth
        });
        alert('Profile updated successfully!');
        window.location.href = '/profile/' + user.uid;
      } catch (error) {
        console.error('Error updating profile: ', error);
        alert('Error updating profile: ' + error.message);
      }
    }
  };

  const PaymentForm = ({ userId, userEmail, userName, stripeCustomerId, onCardSaved }) => {
    const elements = useElements();
    const stripe = useStripe();
  
    const handleSubmit = async (event) => {
      event.preventDefault();
      if (!stripe || !elements) return;
  
      const cardNumberElement = elements.getElement(CardNumberElement);

      // Create a PaymentMethod instead of a token (better validation)
      console.log("Creating payment method with Stripe Elements...");
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardNumberElement,
      });

      if (error) {
        console.error("Error creating payment method:", error);
        alert("Error adding card: " + error.message);
        return;
      }

      if (!paymentMethod || !paymentMethod.id) {
        console.error("Payment method created but no ID returned:", paymentMethod);
        alert("Error: Payment method was not created properly. Please try again.");
        return;
      }

      console.log("Payment method created successfully:", paymentMethod.id);

      const { card } = paymentMethod;
      const expMonth = card.exp_month;
      const expYear = card.exp_year;
      const last4Digits = card.last4;
      
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;

      // Validate expiration date
      if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
        alert("Error: Card expiration date is invalid.");
        return;
      }

      // const confirmCard = async (paymentMethodId) => {
      //   const response = await fetch("/confirm-card-setup", {
      //     method: "POST",
      //     headers: { "Content-Type": "application/json" },
      //     body: JSON.stringify({ paymentMethodId }),
      //   });
      
      //   const data = await response.json();
      //   if (data.error) {
      //     alert("Card verification failed.");
      //     return false;
      //   }
      //   return true;
      // };

      // if (!(await confirmCard(paymentMethod.id))) {
      //   console.error("Error confirming card setup.");
      //   alert("Error confirming card setup.");
      //   return;
      // }

      // try {
      //   // First, retrieve the payment method to check if it exists
      //   const createdPaymentMethod = await stripe.paymentMethods.retrieve(paymentMethod.id);
        
      //   // Check if payment method is already attached to customer
      //   if (createdPaymentMethod.customer !== stripeCustomerId) {
      //     await stripe.paymentMethods.attach(paymentMethod.id, {
      //       customer: stripeCustomerId,
      //     });
      //   }
      // } catch (pmError) {
      //   console.error("PaymentMethod error:", pmError);
      //   throw new Error("Invalid or expired payment method. Please update your payment method.");
      // }
      try {
        // Backend will create a customer if one doesn't exist
        const response = await attachPayment({
          customerId: stripeCustomerId || null, // Pass null if not set, backend will create one
          paymentMethodId: paymentMethod.id,
          userId: userId,
          email: userEmail,
          name: userName,
        });

        if (response.data.success !== true) {
          console.error("Error attaching payment method:", response.data.message);
          alert(response.data.message || "Error attaching payment method. Please try again.");
          return;
        }
        
        // If a new customer was created, the backend returns the new customerId
        const newCustomerId = response.data.customerId;
        
        // Save payment method ID and potentially new customer ID
        const userRef = doc(db, "users", userId);
        const updateData = { 
          paymentMethodId: paymentMethod.id, 
          cardLast4: last4Digits,
          cardExpMonth: expMonth,
          cardExpYear: expYear
        };
        
        // If backend created a new customer, update that too
        if (newCustomerId) {
          updateData.stripeCustomerId = newCustomerId;
          console.log("Updated Stripe customer ID to:", newCustomerId);
        }
        
        await updateDoc(userRef, updateData);
        
      } catch (error) {
        console.error("Error attaching payment method:", error);
        alert("Error attaching payment method: " + (error.message || "Please try again."));
        return;
      }

      onCardSaved(paymentMethod.id, last4Digits, expMonth, expYear);
      

      alert("Card added successfully!");
      // window.location.reload();
    };

    const cardElementOptions = {
      style: {
        base: {
          fontSize: '14px',
          color: '#424770',
          fontFamily: 'Inter, system-ui, sans-serif',
          '::placeholder': {
            color: '#aab7c4',
          },
          padding: '12px',
        },
        invalid: {
          color: '#9e2146',
        },
      },
    };

    return (
      <form onSubmit={handleSubmit} className="mt-4">
        <label className="block text-sm font-semibold text-gray-700 mb-3">Card Details</label>
        <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-4">
          {/* Card Number */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Card Number</label>
            <div className="p-3 border border-gray-200 rounded-lg bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition-all duration-200">
              <CardNumberElement 
                options={cardElementOptions}
                className="w-full"
              />
            </div>
          </div>

          {/* Expiry and CVV in a row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2">Expiry Date</label>
              <div className="p-3 border border-gray-200 rounded-lg bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition-all duration-200">
                <CardExpiryElement 
                  options={cardElementOptions}
                  className="w-full"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2">CVC</label>
              <div className="p-3 border border-gray-200 rounded-lg bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition-all duration-200">
                <CardCvcElement 
                  options={cardElementOptions}
                  className="w-full"
                />
              </div>
            </div>
          </div>

          {/* Security notice */}
          <div className="text-xs text-gray-500 flex items-center justify-center">
            <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span>Your card information is secure and encrypted</span>
          </div>
        </div>
        <button 
          type="submit" 
          className="mt-4 w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 transform hover:scale-[1.02] transition-all duration-200 shadow-lg hover:shadow-xl"
        >
          Save Card
        </button>
      </form>
    );
  };


  

  const handleAddCertification = () => {
    setCertifications([...certifications, '']);
  };

  const handleCertificationChange = (index, value) => {
    const updatedCertifications = [...certifications];
    const existing = updatedCertifications[index];
    if (existing && typeof existing === 'object') {
      updatedCertifications[index] = { ...existing, name: value };
    } else {
      updatedCertifications[index] = { name: value, fileURL: '' };
    }
    setCertifications(updatedCertifications);
  };

  const handleFileUpload = (index, file) => {
    setCertificationFiles({ ...certificationFiles, [index]: file });
  };

  const handleRemoveCertification = (index) => {
    const updatedCertifications = [...certifications];
    updatedCertifications.splice(index, 1);
    setCertifications(updatedCertifications);

    const updatedFiles = { ...certificationFiles };
    delete updatedFiles[index];
    setCertificationFiles(updatedFiles);
  };

  const [isConnectingStripe, setIsConnectingStripe] = useState(false);

  const handleConnectStripe = async (uData, userId) => {
    // Prevent multiple rapid clicks
    if (isConnectingStripe) {
      console.log("Stripe connection already in progress, ignoring duplicate click");
      return;
    }

    setIsConnectingStripe(true);
    try {
      console.log("Connecting to Stripe for user:", uData.email);
      const response = await createStripeAccount({
        email: uData.email,
        sitterId: userId,
        siteUrl: window.location.origin,
      });
  
      const data = response.data;
      if (data.url) {
        console.log("Stripe account", data.isExisting ? "found existing" : "created new", ":", data.accountId);
        window.location.href = data.url; // Redirect to Stripe onboarding
      } else {
        alert("Failed to connect to Stripe.");
        setIsConnectingStripe(false);
      }
    } catch (error) {
      console.error("Error connecting to Stripe:", error);
      alert("Error connecting to Stripe.");
      setIsConnectingStripe(false);
    }
  };

  const checkIfCanEditPayment = async () => {
    const ref = collection(db, "applications");
    const q = query(
      ref,
      where(userData.userType === "parent" ? "parentId" : "sitterId", "==", user.uid),
      where("status", "in", ["Active", "Awaiting Sitter Confirmation", "Completed"])
    );
    const snapshot = await getDocs(q);
    return snapshot.empty;
  };

  const checkIfHasActiveJobs = async () => {
    if (!userData) return false;
    const ref = collection(db, "applications");
    const q = query(
      ref,
      where(userData.userType === "parent" ? "parentId" : "sitterId", "==", user.uid),
      where("status", "in", ["Active", "Awaiting Sitter Confirmation", "Completed"])
    );
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  };

  const handleRemoveStripeAccount = async () => {
    if (!user) return;

    // Check if the user can edit their payment method
    const canEdit = await checkIfCanEditPayment();
    if (!canEdit) {
      alert("You can't edit your payment method while you have active jobs or pending job offers.");
      return;
    }
    
    const confirmRemoval = window.confirm("Are you sure you want to remove your Stripe account?");
    if (!confirmRemoval) return;
  
    try {
      // Call Cloud Function to deauthorize (optional)
      // await fetch("https://us-central1-handpicked-dc4b7.cloudfunctions.net/removeStripeAccount", {
      //   method: "POST",
      //   headers: { "Content-Type": "application/json" },
      //   body: JSON.stringify({ userId: user.uid }),
      // });
  
      // Update Firestore to remove Stripe account ID and all related fields
      await updateDoc(doc(db, "users", user.uid), {
        stripeAccountId: "", // Reset account ID
        stripeAccountStatus: null,
        stripeAccountCreatedAt: null,
        stripeAccountCompletedAt: null,
        updatedAt: serverTimestamp(),
      });
  
      setIsStripeConnected(false);
      alert("Stripe account removed successfully!");
    } catch (error) {
      console.error("Error removing Stripe account:", error);
      alert("Failed to remove Stripe account. Please try again.");
    }
  };


  

  const handleRemoveCard = async () => {
    if (!user) return;
  
    try {
      // For parents: prevent removing card if they have active jobs
      if (userData?.userType === "parent") {
        const hasActiveJobs = await checkIfHasActiveJobs();
        if (hasActiveJobs) {
          alert("You cannot remove your card while you have active jobs. You can change your card by adding a new one.");
          return;
        }
      } else {
        // For sitters: use the existing check
        const canEdit = await checkIfCanEditPayment();
        if (!canEdit) {
          alert("You can't edit your payment method while you have active or pending jobs.");
          return;
        }
      }
  
      const confirm = window.confirm("Are you sure you want to remove your saved card?");
      if (!confirm) return;
  
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        paymentMethodId: "",
        cardLast4: "",
        cardExpMonth: "",
        cardExpYear: "",
      });
  
      setUserData((prev) => ({
        ...prev,
        paymentMethodId: "",
        cardLast4: "",
        cardExpMonth: "",
        cardExpYear: "",
      }));
  
      alert("Card removed successfully.");
    } catch (error) {
      console.error("Error removing card:", error);
      alert("Failed to remove card. Please try again.");
    }
  };
  

  const handleDiscardChanges = () => {
    window.location.href = `/profile/${user.uid}`;
  };

  
  const handleEmailChange = async () => {
    if (!newEmail) {
      alert("Please enter a new email.");
      return;
    }

    try {
      await verifyBeforeUpdateEmail(user, newEmail);
      alert("Verification email sent to your new address. Please verify to complete the update.");
    } catch (error) {
      console.error("Email update error:", error);
      if (error.code === "auth/requires-recent-login") {
        alert("Please log out, then log in again before you update your email.");
      } else {
        alert("Failed to send verification: " + error.message);
      }
    }
  };

  const handleSendVerification = async () => {
    try {
      if (!user) {
        alert('You must be logged in.');
        return;
      }
      await sendEmailVerification(user);
      alert("Verification email sent to " + user.email);
    } catch (error) {
      console.error("Error sending verification email:", error);
      alert("Failed to send verification email.");
    }
  };

  const handleChangePassword = async () => {
    if (!user.email || !user.emailVerified) {
      alert("Please verify your email address.");
      return;
    }

    try {
      await sendPasswordResetEmail(auth, user.email);
      alert("Password reset email sent. Please check your inbox.");
    } catch (error) {
      console.error("Error sending password reset email:", error);
  
      // Optional: Handle common error codes
      switch (error.code) {
        case "auth/invalid-email":
          alert("The email address is not valid.");
          break;
        case "auth/user-not-found":
          alert("No user found with that email.");
          break;
        default:
          alert("Something went wrong. Please try again.");
          break;
      }
    }
  };



  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Edit Profile</h1>
          <p className="text-gray-600 text-lg">Update your information and preferences</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Personal Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                Basic Information
              </h2>
  
      <div className="space-y-4">
        <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white"
                    placeholder="Enter your full name"
          />
        </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Bio</label>
                  <textarea
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white resize-none"
                    rows="4"
                    placeholder="Tell us about yourself..."
                  />
                </div>

                {userData?.userType === 'sitter' && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Date of Birth <InfoTip text="Families only see your age, calculated from this — never your actual birth date." /></label>
                    <input
                      type="date"
                      value={dateOfBirth}
                      onChange={(e) => setDateOfBirth(e.target.value)}
                      max={new Date(Date.now() - 18 * 365.25 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                      min={new Date(Date.now() - 100 * 365.25 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                      className="block w-full min-w-0 box-border px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white"
                    />
                    {dateOfBirth && (
                      <p className="text-sm text-gray-500 mt-2">
                        Families will see your age as: <span className="font-semibold text-gray-700">{getAgeString(dateOfBirth)} old</span>
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <label className="flex items-center justify-between gap-4 cursor-pointer">
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Email Notifications</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {userData?.userType === 'sitter'
                          ? 'Get emailed when new jobs are posted and when parents message you.'
                          : 'Get emailed when sitters apply or send you messages.'}
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={emailNotifications}
                      onChange={(e) => setEmailNotifications(e.target.checked)}
                      className="w-5 h-5 text-blue-500 rounded focus:ring-blue-500 flex-shrink-0"
                    />
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Profile Picture</label>
                  
                  {/* Show existing profile picture if available */}
                  {profilePictureURL && !profilePicturePreview && (
                    <div className="mb-4">
                      <p className="text-sm text-gray-600 mb-2">Current profile picture:</p>
                      <img 
                        src={profilePictureURL} 
                        alt="Current Profile" 
                        className="w-24 h-24 object-cover rounded-xl border-2 border-gray-200"
                      />
            </div>
          )}

                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-blue-400 transition-colors duration-200">
                    <input
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setProfilePicture(file);
                          // Clean up previous preview URL if it exists
                          if (profilePicturePreview && profilePicturePreview !== profilePictureURL) {
                            URL.revokeObjectURL(profilePicturePreview);
                          }
                          setProfilePicturePreview(URL.createObjectURL(file));
                        }
                      }}
                      className="hidden"
                      id="profile-picture"
                      accept="image/*"
                    />
                    <label htmlFor="profile-picture" className="cursor-pointer">
                      <div className="text-gray-500 hover:text-blue-500">
                        <svg className="mx-auto h-12 w-12 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <p className="text-sm">Click to upload or drag and drop</p>
                        <p className="text-xs text-gray-400 mt-1">PNG, JPG, GIF up to 10MB</p>
                      </div>
                    </label>
        </div>

                  {/* Show preview of newly selected image */}
                  {profilePicturePreview && profilePicturePreview !== profilePictureURL && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                      <h3 className="text-sm font-semibold text-blue-800 mb-3">New Profile Picture Preview:</h3>
                      <div className="flex items-center space-x-4">
                        <img 
                          src={profilePicturePreview} 
                          alt="Profile Preview" 
                          className="w-24 h-24 object-cover rounded-xl border-2 border-blue-300"
                        />
                        <div className="flex-1">
                          <p className="text-sm text-blue-700 mb-2">
                            This is how your new profile picture will look. Click "Save Profile" to apply the changes.
                          </p>
          <button
                            onClick={() => {
                              setProfilePicture(null);
                              setProfilePicturePreview(profilePictureURL);
                            }}
                            className="text-sm text-red-600 hover:text-red-700 underline"
                          >
                            Remove new image
          </button>
        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
        </div>
  
            {/* Address Information Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                Address Information
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Street Address</label>
          <input
            type="text"
            value={streetAddress}
            onChange={(e) => setStreetAddress(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white"
                    placeholder="123 Main Street"
          />
        </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">City</label>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white"
              placeholder="City"
            />
          </div>

          <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">State</label>
            <input
              type="text"
              value={state}
              onChange={(e) => setState(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white"
                      placeholder="State"
            />
          </div>

          <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">ZIP Code</label>
            <input
              type="text"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white"
                      placeholder="ZIP"
            />
          </div>
        </div>
              </div>
        </div>
  
            {/* User Type Specific Information */}
          {userData?.userType === 'sitter' ? (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
                  Sitter Information
                </h2>
                
            <div className="space-y-4">
              <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Years of Experience</label>
                <input
                  type="text"
                  value={experience}
                  onChange={(e) => setExperience(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white"
                      placeholder="e.g., 5 years"
                />
              </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">Certifications <InfoTip text="Parents see these on your profile and applications. Upload a photo or PDF of each certificate (CPR, First Aid, etc.) so they can verify it." /></h3>
              {certifications.map((cert, index) => {
                const certName = typeof cert === 'string'
                  ? cert
                  : (typeof cert?.name === 'string'
                      ? cert.name
                      : (typeof cert?.name?.name === 'string' ? cert.name.name : ''));
                const existingFileURL = typeof cert === 'object' && typeof cert?.fileURL === 'string' ? cert.fileURL : '';
                const pendingFile = certificationFiles[index];
                return (
                      <div key={index} className="border border-gray-200 rounded-xl p-4 bg-gray-50 mb-3">
                  <input
                    type="text"
                    value={certName}
                    onChange={(e) => handleCertificationChange(index, e.target.value)}
                    placeholder="Certification Name"
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 mb-3"
                  />
                  {existingFileURL && !pendingFile && (
                    <div className="flex items-center justify-between mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center space-x-2 text-sm text-blue-800">
                        <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className="font-medium">Current file on file</span>
                      </div>
                      <a
                        href={existingFileURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-semibold text-blue-700 hover:text-blue-900 underline"
                      >
                        View
                      </a>
                    </div>
                  )}
                  {pendingFile && (
                    <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                      New file selected: <span className="font-semibold">{pendingFile.name}</span> (will replace current on save)
                    </div>
                  )}
                        <div className="flex items-center space-x-3">
                  <input
                    type="file"
                    onChange={(e) => handleFileUpload(index, e.target.files[0])}
                            className="flex-1 text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  <button
                    onClick={() => handleRemoveCertification(index)}
                            className="px-3 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors duration-200"
                  >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                  </button>
                        </div>
                </div>
                );
              })}

              <button
                onClick={handleAddCertification}
                      className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-all duration-200 hover:bg-blue-50"
              >
                      <svg className="w-5 h-5 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                Add Certification
              </button>
                  </div>
                </div>
            </div>
          ) : (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
                  Family Information
                </h2>

                <div className="space-y-4">
              <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Number of Children</label>
                <input
                  type="number"
                  step="1"
                  min="0"
                  max="20"
                  value={numberOfChildren || ''}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    // Remove leading zeros and parse as integer
                    const count = inputValue === '' ? 0 : parseInt(inputValue, 10) || 0;
                    setNumberOfChildren(count);
                    setChildren((prev) => {
                      const updated = [...prev];
                      if (count > updated.length) {
                        while (updated.length < count) {
                          updated.push({ 
                            id: `child-${Date.now()}-${updated.length}`,
                            name: '', 
                            dateOfBirth: '' 
                          });
                        }
                      } else {
                        updated.length = count;
                      }
                      return updated;
                    });
                  }}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white"
                />
              </div>

              {children.length > 0 && (
                    <div className="space-y-4">
                  {children.map((child, index) => (
                        <div key={child.id || index} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Child {index + 1}
                      </label>
                          <div className="space-y-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Name (Optional)
                              </label>
                              <input
                                type="text"
                                value={child?.name || ''}
                                onChange={(e) => {
                                  const updated = [...children];
                                  updated[index] = {
                                    ...updated[index],
                                    name: e.target.value,
                                  };
                                  setChildren(updated);
                                }}
                                placeholder="Child's name"
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Date of Birth *
                              </label>
                              <input
                                type="date"
                                value={child?.dateOfBirth || ''}
                                onChange={(e) => {
                                  const updated = [...children];
                                  updated[index] = {
                                    ...updated[index],
                                    dateOfBirth: e.target.value,
                                  };
                                  setChildren(updated);
                                }}
                                max={new Date().toISOString().split('T')[0]}
                                className="block w-full min-w-0 box-border px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                              />
                              {child?.dateOfBirth && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Current age: {getAgeString(child.dateOfBirth)}
                                </p>
                              )}
                            </div>
                          </div>
                    </div>
                  ))}
                </div>
              )}
                </div>
            </div>
          )}
        </div>

          {/* Right Column - Account & Payment */}
          <div className="space-y-6">
            {/* Account Settings Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <div className="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
                Account Settings
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">New Email</label>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder={user.email}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white mb-3"
                  />
                  
                  {user && !user.emailVerified && (
                    <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm flex items-center justify-between">
                      <span>Email not verified</span>
                      <button
                        onClick={handleSendVerification}
                        className="text-blue-600 underline hover:text-blue-800 text-sm font-medium"
                      >
                        Send Verification
                      </button>
                    </div>
                  )}

                  <button
                    onClick={handleEmailChange}
                    className="w-full px-4 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-orange-700 transform hover:scale-[1.02] transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    Update Email
                  </button>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={handleChangePassword}
                    className="w-full px-4 py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white font-semibold rounded-xl hover:from-gray-700 hover:to-gray-800 transform hover:scale-[1.02] transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    Reset Password
                  </button>
                </div>
              </div>
            </div>

            {/* Payment Method Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <div className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></div>
                Payment Method
              </h2>
              
          {userData?.cardLast4 ? (
                <div className="space-y-3">
                  <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-green-800 font-medium">Card ending in ****{userData.cardLast4}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {/* Allow changing card even with active jobs for parents */}
                    <Elements stripe={stripePromise}>
                      <PaymentForm 
                        userId={user.uid}
                        userEmail={userData?.email || user.email}
                        userName={name || userData?.name}
                        stripeCustomerId={stripeCustomerId}
                        onCardSaved={(paymentMethodId, last4, expMonth, expYear) => {
                          setUserData(prev => ({
                            ...prev,
                            paymentMethodId: paymentMethodId,
                            cardLast4: last4,
                            cardExpMonth: expMonth,
                            cardExpYear: expYear
                          }));
                        }}
                      />
                    </Elements>
                    <button
                      onClick={handleRemoveCard}
                      className="w-full px-4 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-xl hover:from-red-600 hover:to-red-700 transform hover:scale-[1.02] transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                      Remove Card
                    </button>
                  </div>
            </div>
          ) : (
            <Elements stripe={stripePromise}>
              <PaymentForm 
                userId={user.uid}
                userEmail={userData?.email || user.email}
                userName={name || userData?.name}
                stripeCustomerId={stripeCustomerId}
                onCardSaved={(paymentMethodId, last4, expMonth, expYear) => {
                  setUserData(prev => ({
                    ...prev,
                    paymentMethodId: paymentMethodId,
                    cardLast4: last4,
                    cardExpMonth: expMonth,
                    cardExpYear: expYear
                  }));
                }}
              />
            </Elements>
          )}
        </div>
  
        {/* Stripe Connect for Sitters */}
        {userData?.userType === "sitter" && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mr-3"></div>
                  Stripe Payouts <InfoTip className="ml-2" text="Required to receive payments. After a parent pays, Stripe deposits your money to your bank in 2–7 business days. View payouts anytime at connect.stripe.com/express_login." />
                </h2>
                
            {isStripeConnected ? (
                  <div className="space-y-3">
                    <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-green-800 font-medium">Connected to Stripe</span>
                      </div>
                    </div>
                <button
                  onClick={handleRemoveStripeAccount}
                      className="w-full px-4 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-xl hover:from-red-600 hover:to-red-700 transform hover:scale-[1.02] transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Remove Stripe Account
                </button>
                  </div>
            ) : userData.stripeAccountStatus === 'pending_onboarding' ? (
              <div className="flex items-center justify-between p-4 bg-yellow-50 border border-yellow-200 rounded-xl mb-4">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-yellow-800 font-medium">Stripe Onboarding In Progress</span>
                </div>
                <button
                  onClick={() => handleConnectStripe(userData, user.uid)}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
                >
                  Continue Setup
                </button>
              </div>
            ) : (
              <button
                onClick={() => handleConnectStripe(userData, user.uid)}
                disabled={isConnectingStripe}
                className={`w-full px-4 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-semibold rounded-xl hover:from-emerald-600 hover:to-emerald-700 transform hover:scale-[1.02] transition-all duration-200 shadow-lg hover:shadow-xl ${
                  isConnectingStripe ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isConnectingStripe ? (
                  <span className="flex items-center justify-center space-x-2">
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Connecting...</span>
                  </span>
                ) : (
                  'Connect with Stripe'
                )}
              </button>
            )}
          </div>
        )}
          </div>
      </div>
  
        {/* Action Buttons */}
        <div className="mt-8 flex flex-col sm:flex-row justify-end gap-4">
        <button
          onClick={handleDiscardChanges}
            className="px-8 py-4 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-all duration-200 border border-gray-200 hover:border-gray-300"
        >
          Discard Changes
        </button>
        <button
          onClick={handleSaveProfile}
            className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 transform hover:scale-[1.02] transition-all duration-200 shadow-lg hover:shadow-xl"
        >
          Save Profile
        </button>
        </div>
      </div>
    </div>
  );
};

export default EditProfile;
